<template>
    <div>
        <article :id="nft.id" class="panel large pair entry">
            <header>
                <h3>{{ nft.metadata.title }} <span><small>by</small> {{ nft.metadata.author }}</span></h3>
            </header>
            <div class="primary">
                <template v-if="nft.media.type === 'image'">
                    <a :href="`/assets/megaman/${nft.media.original}`" rel="nofollow">
                        <picture>
                            <template v-if="nft.media.file.substr(-4) === '.gif'">
                                <source
                                    :srcset="`/assets/megaman/${nft.media.file.replace('720.gif', '360.jpg')} 360w, /assets/megaman/${nft.media.file.replace('.gif', '.jpg')} 720w, /assets/megaman/${nft.media.file.replace('720.gif', '720@2x.jpg')} 1440w`"
                                    media="(prefers-reduced-motion:reduce)"
                                    sizes="(min-width:1280px) 720px, calc(100vw - 6.5rem)"
                                    type="image/jpeg">
                            </template>

                            <source
                                :srcset="`/assets/megaman/${nft.media.file.replace('720', '360')} 360w, /assets/megaman/${nft.media.file} 720w, /assets/megaman/${nft.media.file.replace('720', '720@2x')} 1440w`"
                                :type="nft.media.mime"
                                sizes="(min-width:1280px) 720px, calc(100vw - 6.5rem)">

                            <img :alt="nft.metadata.alt ?? nft.metadata.title"
                                 :data-src="`/assets/megaman/${nft.media.file}`"
                                 :height="nft.media.height"
                                 :width="nft.media.width"
                                 loading="lazy"
                                 title="View Original"
                                 src=""/>
                        </picture>
                    </a>
                </template>
                <template v-else-if="nft.media.type === 'video'">
                    <video :data-poster="`/assets/megaman/${nft.media.poster}`" :width="nft.media.width" controls
                           preload="none">
                        <source :src="`/assets/megaman/${nft.media.file}`" :type="nft.media.mime">
                    </video>
                </template>
                <p v-else-if="nft.media.type === 'ascii'">
                    {{ nft.media.original }}
                </p>
            </div>

            <div class="secondary">
                <h4 role="heading" aria-level="3">Metadata</h4>
                <dl>
                    <dt>Description</dt>
                    <dd class="multiline">{{ nft.metadata.description ?? '' }}</dd>

                    <dt>Artist</dt>
                    <dd>{{ nft.metadata.author }}</dd>

                    <template v-if="nft.media.dimensions">
                        <dt class="left half"><abbr title="Multipurpose Internet Mail Extensions">MIME</abbr> Type</dt>
                        <dd class="left half">{{ nft.media.mime }}</dd>
                        <dt class="right half">Dimensions</dt>
                        <dd class="right half">{{ nft.media.dimensions }}</dd>
                    </template>
                    <template v-else>
                        <dt><abbr title="Multipurpose Internet Mail Extensions">MIME</abbr> Type</dt>
                        <dd>{{ nft.media.mime }}</dd>
                    </template>
                </dl>
                <h4 class="uc" role="heading" aria-level="3">NFT</h4>
                <dl>
                    <dt>Token ID</dt>
                    <dd>{{ nft.token.name ?? nft.metadata.title }}</dd>

                    <template v-if="nft.ipfs">
                        <dt>IPFS Files</dt>
                        <dd><a :href="nft.ipfs.media" target="_blank"><span :class="`icon-${nft.media.type}`"></span>Media</a></dd>
                        <dd><a :href="nft.ipfs.json" target="_blank"><span class="icon-code"></span>JSON Metadata</a></dd>
                        <dd v-if="!is_null(nft.ipfs.coa)"><a :href="nft.ipfs.coa" target="_blank"><span class="icon-cert"></span>Certificate of Authenticity</a></dd>
                    </template>

                    <dt class="left half">Total Tokens Issued</dt>
                    <dd class="left half">{{ nft.token.issued.toLocaleString('en-US') }}</dd>

                    <dt class="right half">Amount Burnt</dt>
                    <dd class="right half">{{ nft.token.burnt.toLocaleString('en-US') }}</dd>

                    <template v-if="nft.id.includes('wtf21') || nft.id.includes('wtfascii')">
                        <dt>Price per Token</dt>
                        <dd>{{ nft.token.price.hada.toLocaleString('en-US') }} Hada / {{ nft.token.price.hadalite.toLocaleString('en-US') }} Hadalite</dd>
                    </template>
                </dl>

                <a class="button mb-4" :href="`https://xumm.community/?issuer=${nft.token.tokenIssuer}&currency=${nft.token.hex}&limit=${nft.token.limit}`"
                   rel="noopener nofollow"
                   target="_blank">
                    Set TrustLine
                </a>

                <template v-if="nft.token.upgradable">
                    <template v-if="signedIn">
                        <div class="mt-4">
                            <upgrade-token
                                :token="nft.token"
                                :channel="channel">
                            </upgrade-token>
                        </div>
                    </template>
                    <div v-else>
                        <i class="connect-wallet-text">Connect your wallet to upgrade {{ nft.metadata.title }} to XLS20</i>
                    </div>
                </template>
            </div>
        </article>
    </div>
</template>

<script>
export default {
    name: "MegamanNfToken",

    props: {
        nft: Object,
        channel: String,
        signedIn: Boolean,
    },

    created() {
        this.configureScripts()
    },

    methods: {
        configureScripts: function () {
            document.addEventListener('DOMContentLoaded', function () {
                if ('loading' in HTMLImageElement.prototype) {
                    const images = document.querySelectorAll('img[loading="lazy"]');
                    images.forEach(img => {
                        img.src = img.dataset.src;
                    });
                } else {
                    const script = document.createElement('script');
                    script.src = '/assets/lazysizes.min.js';
                    document.body.appendChild(script);
                }

                const multilines = document.querySelectorAll('.multiline p');
                window.onresize = function () {
                    document.querySelectorAll('.more').forEach(e => e.remove());

                    Array.prototype.forEach.call(multilines, function (element) {
                        if (element.textContent !== '' && element.scrollHeight > element.clientHeight) {
                            const a = document.createElement('span');
                            a.classList.add('more');
                            a.textContent = 'More';
                            element.parentNode.insertBefore(a, element.nextSibling);
                        }
                    });
                }
                window.dispatchEvent(new Event('resize'));

                function hasClass(elem, className) {
                    return elem.classList.contains(className);
                }

                document.addEventListener('click', function (e) {
                    if (hasClass(e.target, 'more')) {
                        e.preventDefault();

                        const parent = e.target.previousSibling;
                        parent.style.display = parent.style.display === 'block' ? '-webkit-box' : 'block';
                        e.target.textContent = e.target.textContent === 'More' ? 'Less' : 'More';
                    }
                });

            });

            window.addEventListener('load', function () {
                const videos = [].slice.call(document.querySelectorAll('video'));
                videos.forEach(function (video) {
                    video.poster = video.dataset.poster;
                });
            });
        }
    }
}
</script>

<style>
.entry img {
    background: #fff no-repeat center center;
}
</style>
